import { useCallback, useEffect, useState } from "react";
import { ExternalLink, Loader2 } from "lucide-react";
import api from "@/lib/api";

// Private review page for the horizon scan. Not linked anywhere, not indexed,
// and every API call needs the admin key (HORIZON_ADMIN_KEY on Render).
const KEY_STORE = "electra-horizon-key";
const readKey = () => { try { return sessionStorage.getItem(KEY_STORE) || ""; } catch { return ""; } };
const saveKey = (k) => { try { sessionStorage.setItem(KEY_STORE, k); } catch { /* private mode */ } };

const ItemCard = ({ item, onSave, busy }) => {
  const [note, setNote] = useState(item.note || "");
  const [actBy, setActBy] = useState(item.act_by || "");
  return (
    <li data-testid="horizon-admin-item" className="border-2 border-ink/15 bg-paper p-5 sm:p-6">
      <p className="font-mono text-xs uppercase tracking-[0.18em] text-ink/45">
        {item.source} · {new Date(item.published).toLocaleDateString("en-GB")}
      </p>
      <a href={item.url} target="_blank" rel="noopener noreferrer" className="mt-1.5 inline-flex items-start gap-2 font-display text-lg font-semibold leading-snug hover:text-pink">
        {item.title} <ExternalLink size={15} className="mt-1.5 shrink-0" />
      </a>
      <label className="mt-4 block text-sm font-semibold">What it means for members (two lines)</label>
      <textarea
        value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={1200}
        className="mt-1.5 w-full border-2 border-ink/20 bg-transparent px-3 py-2 text-sm outline-none focus:border-pink"
      />
      <div className="mt-3 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-sm font-semibold">Act by (optional)</label>
          <input type="date" value={actBy} onChange={(e) => setActBy(e.target.value)} className="mt-1.5 border-2 border-ink/20 bg-transparent px-3 py-2 text-sm" />
        </div>
        <button disabled={busy} onClick={() => onSave(item.id, "approved", note, actBy)} data-testid="horizon-approve" className="bg-pink px-5 py-2.5 text-sm font-bold text-ink disabled:opacity-50">
          Approve
        </button>
        <button disabled={busy} onClick={() => onSave(item.id, "rejected", note, actBy)} data-testid="horizon-reject" className="border-2 border-ink/30 px-5 py-2 text-sm font-bold disabled:opacity-50">
          Bin
        </button>
      </div>
    </li>
  );
};

export default function AdminHorizon() {
  const [key, setKey] = useState(readKey());
  const [keyInput, setKeyInput] = useState("");
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots"; m.content = "noindex, nofollow";
    document.head.appendChild(m);
    document.title = "Horizon scan review";
    return () => m.remove();
  }, []);

  const headers = { "X-Admin-Key": key };

  const load = useCallback(async () => {
    if (!key) return;
    setError("");
    try {
      const r = await api.get("/horizon/admin/items", { params: { status }, headers: { "X-Admin-Key": key } });
      setItems(r.data.items || []);
    } catch {
      setError("That key didn't work, or the review page isn't switched on yet.");
      setItems([]);
    }
  }, [key, status]);

  useEffect(() => { load(); }, [load]);

  const save = async (id, newStatus, note, actBy) => {
    setBusy(true);
    try {
      await api.post(`/horizon/admin/items/${id}`, { status: newStatus, note, act_by: actBy }, { headers });
      setItems((list) => list.filter((i) => i.id !== id));
    } catch {
      setError("Couldn't save that one. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const collect = async () => {
    setBusy(true);
    try { await api.post("/horizon/admin/collect", {}, { headers, timeout: 180000 }); await load(); }
    catch { setError("Collecting failed. It runs by itself every night anyway."); }
    finally { setBusy(false); }
  };

  if (!key) {
    return (
      <main className="bg-paper pb-24 pt-32 sm:pt-40">
        <div className="container-x max-w-md">
          <h1 className="font-display text-3xl font-semibold">Horizon scan review</h1>
          <form className="mt-6 space-y-3" onSubmit={(e) => { e.preventDefault(); saveKey(keyInput.trim()); setKey(keyInput.trim()); }}>
            <label className="block text-sm font-semibold">Review key</label>
            <input type="password" value={keyInput} onChange={(e) => setKeyInput(e.target.value)} data-testid="horizon-key-input" className="w-full border-2 border-ink/20 bg-transparent px-3 py-2" />
            <button className="bg-pink px-5 py-2.5 font-bold text-ink">Open</button>
          </form>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="horizon-admin-page" className="bg-ink/[0.03] pb-24 pt-32 sm:pt-40">
      <div className="container-x max-w-3xl">
        <h1 className="font-display text-3xl font-semibold">Horizon scan review</h1>
        <p className="mt-2 text-sm text-ink/60">
          Approved items go on the public page as headlines only. Your note and date are for members. Nothing is shown until you approve it.
        </p>
        <div className="mt-6 flex flex-wrap gap-2">
          {["pending", "approved", "rejected"].map((s) => (
            <button key={s} onClick={() => setStatus(s)} className={`px-4 py-2 text-sm font-bold ${status === s ? "bg-ink text-paper" : "border-2 border-ink/20"}`}>
              {s === "pending" ? "Waiting" : s === "approved" ? "Approved" : "Binned"}
            </button>
          ))}
          <button disabled={busy} onClick={collect} className="ml-auto border-2 border-ink/20 px-4 py-2 text-sm font-bold disabled:opacity-50">
            {busy ? <Loader2 size={16} className="animate-spin" /> : "Check feeds now"}
          </button>
        </div>
        {error && <p className="mt-4 text-sm font-semibold text-signal">{error}</p>}
        {items.length === 0 && !error ? (
          <p className="mt-8 text-ink/60">Nothing here.</p>
        ) : (
          <ul className="mt-6 space-y-4">
            {items.map((it) => <ItemCard key={it.id} item={it} onSave={save} busy={busy} />)}
          </ul>
        )}
      </div>
    </main>
  );
}
